/**
 * Mutation Queue Storage
 *
 * Manages a queue of mutations in IndexedDB for offline support.
 * Mutations are persisted and can be synced when the app goes back online.
 *
 * Uses shared storage with React Query persistence for unified offline data management.
 */

import { v4 as uuidv4 } from 'uuid';

import { requestBackgroundSync } from './backgroundSync';
import { getOfflineStorage, saveOfflineStorage } from './reactQueryPersistence';
import { MutationOperation, MutationOperationType, MutationQueue, MutationStatus } from './types';

/**
 * Broadcast a custom event when queue updates
 * This allows components to react to queue changes
 */
function broadcastQueueUpdate(): void {
  if (typeof window !== 'undefined') {
    const event = new CustomEvent('offlineMutationQueueUpdate');
    window.dispatchEvent(event);
  }
}

/**
 * Get all mutations from the queue
 */
export async function getMutationQueue(): Promise<MutationQueue> {
  const storage = await getOfflineStorage();
  return storage.mutations ?? [];
}

/**
 * Save the entire mutation queue
 */
async function saveMutationQueue(queue: MutationQueue): Promise<void> {
  const storage = await getOfflineStorage();
  storage.mutations = queue;
  await saveOfflineStorage(storage);
  broadcastQueueUpdate();
}

/**
 * Check if a mutation payload has the correct format for batch API
 * The batch API expects: { input: { propertyId: number, ... } }
 */
function isValidMutationPayload(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') return false;
  const p = payload as Record<string, unknown>;
  if (!p.input || typeof p.input !== 'object') return false;
  const input = p.input as Record<string, unknown>;
  return typeof input.propertyId === 'number';
}

/**
 * Remove invalid mutations from the queue
 * This cleans up mutations queued with the old format
 */
export async function cleanupInvalidMutations(): Promise<number> {
  const queue = await getMutationQueue();
  const validMutations = queue.filter((mutation) => {
    return isValidMutationPayload(mutation.payload);
  });

  const removedCount = queue.length - validMutations.length;
  if (removedCount > 0) {
    await saveMutationQueue(validMutations);
  }

  return removedCount;
}

/**
 * Add a new mutation to the queue
 */
export async function addMutation(
  mutation: Omit<MutationOperation, 'id' | 'timestamp' | 'status' | 'retryCount'>,
): Promise<MutationOperation> {
  const queue = await getMutationQueue();

  const newMutation: MutationOperation = {
    ...mutation,
    id: uuidv4(),
    timestamp: Date.now(),
    status: 'pending',
    retryCount: 0,
  };

  queue.push(newMutation);
  await saveMutationQueue(queue);

  // Request background sync so mutations can be synced even if app is closed
  // Wrapped in try-catch for robustness in test environments
  try {
    const syncPromise = requestBackgroundSync();
    if (syncPromise && typeof syncPromise.catch === 'function') {
      syncPromise.catch(() => {
        // Non-critical - sync will still happen when app is opened
      });
    }
  } catch {
    // Background sync not available
  }

  return newMutation;
}

/**
 * Remove a mutation from the queue by ID
 */
export async function removeMutation(id: string): Promise<boolean> {
  const queue = await getMutationQueue();
  const index = queue.findIndex((m) => m.id === id);

  if (index === -1) {
    return false;
  }

  queue.splice(index, 1);
  await saveMutationQueue(queue);

  return true;
}

/**
 * Update a mutation's status
 */
export async function updateMutationStatus(
  id: string,
  status: MutationStatus,
  error?: string,
): Promise<MutationOperation | null> {
  const queue = await getMutationQueue();
  const mutation = queue.find((m) => m.id === id);

  if (!mutation) {
    return null;
  }

  mutation.status = status;

  if (error) {
    mutation.lastError = error;
  }

  if (status === 'failed') {
    mutation.retryCount += 1;
  }

  await saveMutationQueue(queue);

  return mutation;
}

/**
 * Get a specific mutation by ID
 */
export async function getMutationById(id: string): Promise<MutationOperation | null> {
  const queue = await getMutationQueue();
  return queue.find((m) => m.id === id) ?? null;
}

/**
 * Get all mutations for a specific property
 */
export async function getMutationsByProperty(propertyId: number): Promise<MutationQueue> {
  const queue = await getMutationQueue();
  return queue.filter((m) => m.propertyId === propertyId);
}

/**
 * Get all pending mutations (not yet synced or failed)
 * This includes failed mutations that should be retried
 */
export async function getPendingMutations(): Promise<MutationQueue> {
  const queue = await getMutationQueue();
  return queue.filter((m) => m.status === 'pending' || m.status === 'failed');
}

/**
 * Get all mutations with 'pending' status only (excluding failed)
 */
export async function getPendingOnlyMutations(): Promise<MutationQueue> {
  const queue = await getMutationQueue();
  return queue.filter((m) => m.status === 'pending');
}

/**
 * Get count of pending mutations
 */
export async function getPendingMutationCount(): Promise<number> {
  const pending = await getPendingMutations();
  return pending.length;
}

/**
 * Get mutations by entity type
 */
export async function getMutationsByEntity(entity: string): Promise<MutationQueue> {
  const queue = await getMutationQueue();
  return queue.filter((m) => m.entity === entity);
}

/**
 * Get mutations by operation type
 */
export async function getMutationsByType(type: MutationOperationType): Promise<MutationQueue> {
  const queue = await getMutationQueue();
  return queue.filter((m) => m.type === type);
}

/**
 * Clear all mutations from the queue
 */
export async function clearMutationQueue(): Promise<void> {
  const storage = await getOfflineStorage();
  storage.mutations = [];
  await saveOfflineStorage(storage);
  broadcastQueueUpdate();
}

/**
 * Clear only completed mutations
 */
export async function clearCompletedMutations(): Promise<number> {
  const queue = await getMutationQueue();
  const remaining = queue.filter((m) => m.status !== 'completed');
  const clearedCount = queue.length - remaining.length;

  await saveMutationQueue(remaining);

  return clearedCount;
}

/**
 * Reset failed mutations to pending status for retry
 */
export async function resetFailedMutations(): Promise<number> {
  const queue = await getMutationQueue();

  const resetCount = queue.reduce((count, mutation) => {
    if (mutation.status === 'failed') {
      // eslint-disable-next-line no-param-reassign
      mutation.status = 'pending';
      // eslint-disable-next-line no-param-reassign
      mutation.lastError = undefined;
      return count + 1;
    }
    return count;
  }, 0);

  if (resetCount > 0) {
    await saveMutationQueue(queue);
  }

  return resetCount;
}

/**
 * Mark all syncing mutations as pending (recovery after crash)
 */
export async function recoverStuckMutations(): Promise<number> {
  const queue = await getMutationQueue();

  const recoveredCount = queue.reduce((count, mutation) => {
    if (mutation.status === 'syncing') {
      // eslint-disable-next-line no-param-reassign
      mutation.status = 'pending';
      return count + 1;
    }
    return count;
  }, 0);

  if (recoveredCount > 0) {
    await saveMutationQueue(queue);
  }

  return recoveredCount;
}

/**
 * Check if there are any mutations in the queue
 */
export async function hasPendingMutations(): Promise<boolean> {
  const count = await getPendingMutationCount();
  return count > 0;
}

/**
 * Get the oldest pending mutation (for FIFO processing)
 * Only returns mutations with 'pending' status to avoid processing failed mutations in a loop
 */
export async function getNextPendingMutation(): Promise<MutationOperation | null> {
  const pending = await getPendingOnlyMutations();

  if (pending.length === 0) {
    return null;
  }

  // Sort by timestamp to get FIFO order
  pending.sort((a, b) => a.timestamp - b.timestamp);

  return pending[0];
}

/**
 * Get the next mutation that should be retried (failed but not exceeded max retries)
 */
export async function getNextRetryableMutation(maxRetries: number): Promise<MutationOperation | null> {
  const queue = await getMutationQueue();
  const retryable = queue.filter((m) => m.status === 'failed' && m.retryCount < maxRetries);

  if (retryable.length === 0) {
    return null;
  }

  // Sort by timestamp to get FIFO order
  retryable.sort((a, b) => a.timestamp - b.timestamp);

  return retryable[0];
}

/**
 * Subscribe to queue updates
 */
export function onQueueUpdate(callback: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  window.addEventListener('offlineMutationQueueUpdate', callback);

  return () => {
    window.removeEventListener('offlineMutationQueueUpdate', callback);
  };
}
